const HERO_IMAGE =
  'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?auto=format&fit=crop&w=1600&q=80';

const DIAGRAM_LEGEND = [
  { color: '#fbbf24', label: 'GK — Goalkeeper' },
  { color: '#3b82f6', label: 'DEF — Defenders' },
  { color: '#22c55e', label: 'MID — Midfielders' },
  { color: '#ef4444', label: 'FWD — Forwards' },
];

/** 2-2-2 shape for adult 7v7: shared attacking load, less solo striker running */
const BASE_PLAYERS = [
  { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
  { label: 'LB', roleType: 'DEF', x: 20, y: 22 },
  { label: 'RB', roleType: 'DEF', x: 20, y: 43 },
  { label: 'LCM', roleType: 'MID', x: 36, y: 26 },
  { label: 'RCM', roleType: 'MID', x: 36, y: 39 },
  { label: 'LF', roleType: 'FWD', x: 58, y: 18 },
  { label: 'RF', roleType: 'FWD', x: 58, y: 47 },
];

/** Featured guide: 7v7 soccer formation for adult rec players ages 35+ */
export const SEVEN_V_SEVEN_FORMATION_OVER_35_POST = {
  id: 'featured-7v7-formation-over-35',
  slug: '7v7-soccer-formation-over-35-guide',
  title: '7v7 Soccer Formation for Ages 35+: 2-2-2 Shape, Roles & Movement Diagrams',
  excerpt:
    'Adult rec 7v7 tactics built for players 35 and up—energy-smart 2-2-2 positioning, role clarity, compact defending, and half-pitch diagrams so you play smart instead of sprinting all game.',
  publishedAt: '2026-07-28T00:00:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Tactics Desk',
  blogCategory: 'exercise',
  audience: 'Ages 35+',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Formation overview',
      items: [
        { value: '2-2-2', label: 'Recommended shape' },
        { value: '35+', label: 'Adult rec focus' },
        { value: '7', label: 'Players on field' },
        { value: '5 diagrams', label: 'Shape & movement' },
      ],
    },
    {
      type: 'panel',
      title: 'Why this guide is different for 35+',
      paragraphs: [
        'Youth 7v7 guides push high pressing, constant runs, and a lone striker doing everything. At **35 and older**, the game is still competitive—but recovery is slower, hamstrings are real, and the smartest teams win with **shape, spacing, and passing** instead of athletic overload.',
        'This guide recommends the **2-2-2** (1 goalkeeper, 2 defenders, 2 central midfielders, 2 forwards). Two forwards share the attacking load; two mids control tempo without covering the entire width; defenders stay organized instead of bombing forward every possession.',
        'Diagrams use **goal on the left**, **attack toward the right**. Solid arrows = passes; dashed orange = off-ball runs. Pace yourself—win the second half by being the team that still has legs.',
      ],
      chips: ['7v7', '2-2-2', 'Adult rec', 'Ages 35+'],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 1 — Default 2-2-2 shape (at rest)',
      caption:
        'Balanced block: defenders narrow but not flat; midfield pair stays connected; forwards stagger high and wide so one can check in while the other stays high.',
      legend: DIAGRAM_LEGEND,
      players: BASE_PLAYERS,
      arrows: [],
    },
    {
      type: 'table',
      title: 'Each position explained (35+ rec priorities)',
      intro:
        'Assign roles before kickoff and agree on substitution plan. Adult leagues often allow rolling subs—use them to keep intensity high without burning out starters.',
      headers: ['Pos.', 'Role', 'Primary job (35+)', 'Energy-smart habits'],
      rows: [
        [
          'GK',
          'Goalkeeper',
          'Organize the line; distribute quickly so outfield players do not chase.',
          'Talk constantly; throw or roll to feet; avoid risky long kicks unless wind is at your back.',
        ],
        [
          'LB / RB',
          'Defenders',
          'Delay attackers; win the ball cleanly; pass simple to midfield.',
          'Jockey instead of diving in; recover walking speed if beat; do not overlap unless CM covers.',
        ],
        [
          'LCM / RCM',
          'Central midfielders',
          'Control tempo; link defense to attack; switch play when one side is crowded.',
          'One presses, one holds—never both chase; receive on the half-turn; three-touch max in your own third.',
        ],
        [
          'LF / RF',
          'Forwards',
          'Stretch the field; combine with each other; force defenders to make a choice.',
          'Alternate who checks deep so the other stays high; diagonal runs beat straight sprints; press as a pair, not a marathon.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Goalkeeper (GK) — ages 35+',
      paragraphs: [
        'Many adult teams rotate keepers. Whoever is in goal sets the tone: calm distribution beats highlight-reel throws.',
      ],
      list: [
        'Stay connected to your back line—call “step,” “hold,” and “squeeze” early.',
        'Distribution: prioritize safe passes to a defender’s open foot; long balls only when forwards are already facing goal.',
        'Set position: slightly off center toward the near post when the ball is wide; one step out on cutbacks.',
        'Skip hero 1v1 charges outside the box unless you are sure—stay on your feet and force a bad angle.',
      ],
    },
    {
      type: 'panel',
      title: 'Defenders (LB & RB)',
      paragraphs: [
        'At 35+, the cheapest defensive action is **delay and show inside**. Tackles are for when you are sure; otherwise force a pass backward or wide.',
      ],
      list: [
        'Stay goal-side; one defender steps, the other covers central space.',
        'Do not chase into midfield—let mids press in front of you.',
        'In possession: pass to the nearest midfielder; carry one touch only if the lane is open.',
        'Recovery: if beat, sprint to the near post first, then reorganize—do not stand and argue.',
      ],
    },
    {
      type: 'panel',
      title: 'Central midfielders (LCM & RCM)',
      paragraphs: [
        'The midfield pair is the engine room for adult 7v7. You do not need to run the most—you need to **be in the right place** when the ball moves.',
      ],
      list: [
        'Press in tandem: when LCM steps, RCM screens the pass behind; swap roles when the ball switches.',
        'Offer angles at 45°—not straight lines that invite turnovers.',
        'Switch play in two touches when the far forward is isolated 1v1.',
        'Defensive default: protect the center of the field; let opponents have harmless wide areas.',
      ],
    },
    {
      type: 'panel',
      title: 'Forwards (LF & RF)',
      paragraphs: [
        'Two forwards beat one tired striker. Work in **pairs**: one checks to feet, one threatens in behind—then swap.',
      ],
      list: [
        'Stagger runs: if LF drops to link, RF stays high on the shoulder of the last defender.',
        'Combine: wall passes and overlap give you goals without 40-yard sprints.',
        'Press together for 6–8 seconds after losing the ball, then drop into the block—do not chase all game.',
        'Finish efficiently: first-time shots and near-post runs beat fancy dribbling in tight adult leagues.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 2 — Compact defensive block (save your legs)',
      caption:
        'When protecting a lead or recovering from a long attack: drop into a tight 2-2-2, squeeze central space, and force low-percentage shots from distance.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 16, y: 24 },
        { label: 'RB', roleType: 'DEF', x: 16, y: 41 },
        { label: 'LCM', roleType: 'MID', x: 28, y: 28 },
        { label: 'RCM', roleType: 'MID', x: 28, y: 37 },
        { label: 'LF', roleType: 'FWD', x: 38, y: 22 },
        { label: 'RF', roleType: 'FWD', x: 38, y: 43 },
      ],
      arrows: [
        { from: { x: 38, y: 22 }, to: { x: 44, y: 32.5 }, style: 'run', label: 'Short press' },
        { from: { x: 28, y: 28 }, to: { x: 34, y: 32.5 }, style: 'run', label: 'Screen' },
      ],
    },
    {
      type: 'panel',
      title: 'Defending as a 35+ team',
      list: [
        'Default to the compact block in Diagram 2 when the opponent has sustained possession.',
        'Press in waves: forwards for 6–8 seconds, then mids, then full line drop—never all-out chase for 90 minutes.',
        'Communicate switches: “ball left,” “step,” “hold”—talking saves sprints.',
        'Fouls in dangerous areas are sometimes smarter than a stretched recovery run—know your league rules.',
        'Halftime reset: agree whether you are pressing or sitting before the second half starts.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 3 — Patient build-up (possession without panic)',
      caption:
        'Adult rec teams lose the ball by rushing. Move the ball GK → defender → midfielder → far forward only when the pass is on.',
      legend: DIAGRAM_LEGEND,
      players: BASE_PLAYERS,
      arrows: [
        { from: { x: 8, y: 32.5 }, to: { x: 20, y: 22 }, style: 'pass', label: '1' },
        { from: { x: 20, y: 22 }, to: { x: 36, y: 26 }, style: 'pass', label: '2' },
        { from: { x: 36, y: 26 }, to: { x: 36, y: 39 }, style: 'pass', label: '3' },
        { from: { x: 36, y: 39 }, to: { x: 58, y: 47 }, style: 'pass', label: '4' },
      ],
    },
    {
      type: 'panel',
      title: 'Build-up coaching points (adult rec)',
      paragraphs: [
        'You do not need 15 passes to score—but you do need **one safe pass** to escape pressure. Train the four-pass pattern until midfielders demand the ball with their body shape.',
      ],
      list: [
        'Goalkeeper: play to the defender’s open side, not into a closed body.',
        'Defenders: first look is central to a midfielder; second look is wide to a forward checking in.',
        'Midfielders: receive side-on; one touch to switch beats three touches in a crowd.',
        'Forwards: one checks, one stays high—always give the passer two options.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 4 — Attacking shape (staggered runs, not constant sprints)',
      caption:
        'In the final third: midfielders arrive late for cut-backs; forwards alternate near-post and far-post runs; defenders hold unless you are chasing a goal.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 24, y: 20 },
        { label: 'RB', roleType: 'DEF', x: 24, y: 45 },
        { label: 'LCM', roleType: 'MID', x: 48, y: 30 },
        { label: 'RCM', roleType: 'MID', x: 52, y: 38 },
        { label: 'LF', roleType: 'FWD', x: 72, y: 16 },
        { label: 'RF', roleType: 'FWD', x: 68, y: 48 },
      ],
      arrows: [
        { from: { x: 48, y: 30 }, to: { x: 72, y: 16 }, style: 'pass', label: 'Cut-back' },
        { from: { x: 68, y: 48 }, to: { x: 78, y: 32.5 }, style: 'run', label: 'Near post' },
        { from: { x: 72, y: 16 }, to: { x: 80, y: 28 }, style: 'run', label: 'Far post' },
      ],
    },
    {
      type: 'panel',
      title: 'Movement in attack — energy-smart patterns',
      orderedList: [
        'LF and RF alternate who checks to midfield—never both drop at once.',
        'LCM arrives late into the top of the box for rebounds; RCM holds for second balls.',
        'Use the width of the field with passes, not endless overlapping runs from defenders.',
        'After a shot, one forward rebounds, one stays high—quick rest for the third attack wave.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 5 — Quick counter (when you win it clean)',
      caption:
        'Best chance for 35+ teams: win the ball in midfield, play forward early, support with one timed run—not four players sprinting at once.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 20, y: 22 },
        { label: 'RB', roleType: 'DEF', x: 20, y: 43 },
        { label: 'LCM', roleType: 'MID', x: 42, y: 32.5 },
        { label: 'RCM', roleType: 'MID', x: 46, y: 36 },
        { label: 'LF', roleType: 'FWD', x: 70, y: 22 },
        { label: 'RF', roleType: 'FWD', x: 74, y: 42 },
      ],
      arrows: [
        { from: { x: 42, y: 32.5 }, to: { x: 70, y: 22 }, style: 'pass', label: 'Early' },
        { from: { x: 74, y: 42 }, to: { x: 82, y: 35 }, style: 'run', label: 'Support' },
      ],
    },
    {
      type: 'table',
      title: '2-2-2 vs 2-3-1 for ages 35+',
      intro:
        'Both shapes work in adult 7v7. Choose based on your roster fitness and how many willing runners you have.',
      headers: ['Formation', 'Best for 35+ when…', 'Watch out for…'],
      rows: [
        [
          '2-2-2 (this guide)',
          'Shared attacking load; mixed fitness levels; you want less solo striker running',
          'Can get narrow if wide mids are missing—forwards must stay wide',
        ],
        [
          '2-3-1',
          'You have one fit striker and two wide mids who track back',
          'Lone striker gasses out; CM covers too much ground',
        ],
        [
          '3-2-1',
          'Opponent plays two up top; you prioritize not conceding',
          'Fewer outlets in attack; needs patient build-up',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Quick reference — 2-2-2 for adult rec',
      headers: ['Phase', 'GK', 'DEF', 'MID', 'FWD'],
      rows: [
        [
          'We have the ball',
          'Distribute safe and fast',
          'Hold line; pass to mids',
          'Control tempo; switch play',
          'Stagger runs; combine',
        ],
        [
          'They have the ball',
          'Organize; claim crosses',
          'Delay; tackle when sure',
          'Screen center; press in pairs',
          'Press 6–8 sec, then drop',
        ],
        [
          'Win the ball',
          'Stay home',
          'Hold 2 seconds',
          'Play forward if on',
          'One sprint; one supports',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Match-day habits for players 35+',
      list: [
        'Warm up 15 minutes: jog, dynamic stretches, passing in pairs—no static stretching cold.',
        'Hydrate before you feel thirsty; electrolytes help on hot Ohio summer evenings.',
        'Sub every 8–12 minutes if your league allows rolling changes—fresh legs beat heroic 40-minute shifts.',
        'Communicate injuries early; a pulled hamstring becomes a season problem if you “run it off.”',
        'Cool down 5 minutes: light jog and calf/hamstring stretch after the final whistle.',
        'Skip Tuesday hero tackles if you have a desk job Wednesday—pick your moments.',
      ],
    },
    {
      type: 'panel',
      title: 'Pre-match team talk (2 minutes)',
      orderedList: [
        'Confirm formation: 2-2-2, who is GK, who subs when.',
        'Defensive default: compact block or press—pick one for the first half.',
        'Attack plan: build wide or play through the middle based on opponent weakness.',
        'Set piece: who takes corners and who attacks the near post.',
        'Reminder: pass and move beats sprint and hope at our age.',
      ],
      callout:
        'Coaching youth 7v7 too? See our 2-3-1 youth guide for the classic kids’ shape and higher-tempo movement patterns.',
    },
  ],
  relatedPostsTitle: 'More soccer training guides',
  relatedPosts: [
    {
      slug: '7v7-soccer-formation-2-3-1-guide',
      title: '7v7 Soccer Formation Guide: 2-3-1 Explained with Movement Diagrams',
      blurb: 'youth-focused 2-3-1 shape and high-tempo runs.',
    },
    {
      slug: 'soccer-defender-drills-free-youtube-videos',
      title: 'Soccer Defender Drills + Free YouTube Training Videos',
      blurb: 'delay, tackling, and positioning for backs.',
    },
    {
      slug: 'soccer-forward-striker-drills-free-youtube-videos',
      title: 'Soccer Forward & Striker Drills + Free YouTube Videos',
      blurb: 'finishing and movement for forwards.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'find a 7v7 pitch for your adult league.',
    },
  ],
  referenceLinks: [
    {
      label: 'FIFA 11+ warm-up program (injury prevention)',
      url: 'https://www.fifa.com/technical/football-technology/standardisation/news/f-11-injury-prevention',
    },
    {
      label: 'U.S. Soccer — adult grassroots resources',
      url: 'https://www.ussoccer.com/',
    },
  ],
};
